import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { OrderingOption } from 'generated/graphql-types';
import { of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { MailboxFilterOptions } from '../models/mailbox-filter-option';
import { MailboxRegisterOptions } from '../models/mailbox-register-options';
import { MailboxRegisterListItem, MailboxRegisterResults } from '../models/mailbox-register-results.model';
import {
   GetMailboxRegister,
   GetMailboxRegister_mailboxesConnection_items,
} from '../queries/generated/GetMailboxRegister';
import { MailboxRegisterApiService } from '../services/mailbox-register-api.service';
import { mailboxRegisterAdapterActions } from './mailbox-register.actions';
import {
   MailboxRegisterState,
   selectEndCursor,
   selectOrderingOptions,
   selectQuickFilters,
} from './mailbox-register.reducer';
import * as MailboxRegisterSelectors from './mailbox-register.selectors';
import { selectFilters } from './mailbox-register.selectors';

@Injectable()
export class MailboxRegisterEffects {
   loadMailboxList$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(mailboxRegisterAdapterActions.pageEnter),
         withLatestFrom(this.store.pipe(select(selectFilters)), this.store.pipe(select(selectOrderingOptions))),
         switchMap(([, filters, orderingOptions]) => {
            return this.loadNewData(filters, null, orderingOptions);
         })
      );
   });

   loadMailboxPage$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(mailboxRegisterAdapterActions.loadPage),
         withLatestFrom(
            this.store.pipe(select(MailboxRegisterSelectors.selectFilters)),
            this.store.pipe(select(selectEndCursor)),
            this.store.pipe(select(selectOrderingOptions)),
            this.store.pipe(select(selectQuickFilters))
         ),
         switchMap(([action, filters, endCursor, orderingOptions, quickFilters]) => {
            return this.loadNewPage(endCursor, filters, quickFilters, orderingOptions);
         })
      );
   });

   quickFilterChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(mailboxRegisterAdapterActions.quickFilterChanged),
         withLatestFrom(
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(MailboxRegisterSelectors.selectFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, quickFilters, filters, orderingOptions]) => {
            return this.loadNewData(filters, quickFilters, orderingOptions);
         })
      );
   });

   orderingOptionsChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(mailboxRegisterAdapterActions.orderingOptionsChanged),
         withLatestFrom(
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions)),
            this.store.pipe(select(selectFilters))
         ),
         switchMap(([action, quickfilters, orderingOptions, filters]) => {
            return this.loadNewData(filters, quickfilters, orderingOptions);
         })
      );
   });

   constructor(
      private actions$: Actions,
      private store: Store<MailboxRegisterState>,
      private mailboxRegisterApiService: MailboxRegisterApiService
   ) {}

   private loadNewData(
      filters: MailboxFilterOptions[],
      quickFilters: MailboxRegisterOptions | null = null,
      orderingOptions: OrderingOption[] | null = null
   ) {
      return this.mailboxRegisterApiService.getMailbox(null, filters, quickFilters, orderingOptions).pipe(
         map((result) => {
            return mailboxRegisterAdapterActions.loadSuccess(
               this.convertGetMailboxResultToLoadSuccessProps(result.data)
            );
         }),
         catchError((error) => {
            return of(mailboxRegisterAdapterActions.loadFailure(error));
         })
      );
   }

   private loadNewPage(
      endCursor: string | null,
      filters: MailboxFilterOptions[],
      quickFilters: MailboxRegisterOptions | null = null,
      orderingOptions: OrderingOption[] | null = null
   ) {
      return this.mailboxRegisterApiService.getMailbox(endCursor, filters, quickFilters, orderingOptions).pipe(
         map((result) => {
            return mailboxRegisterAdapterActions.loadPageSuccess(
               this.convertGetMailboxResultToLoadSuccessProps(result.data)
            );
         }),
         catchError((error) => {
            return of(mailboxRegisterAdapterActions.loadPageFailure(error));
         })
      );
   }

   private convertGetMailboxResultToLoadSuccessProps(getMailbox: GetMailboxRegister): MailboxRegisterResults {
      return {
         endCursor: getMailbox.mailboxesConnection.pageInfo.endCursor,
         hasNextPage: getMailbox.mailboxesConnection.pageInfo.hasNextPage,
         items: getMailbox.mailboxesConnection.items.map(this.convertMailboxRegisterListItem),
         totalCount: getMailbox.mailboxesConnection.totalCount,
      };
   }

   private convertMailboxRegisterListItem(
      getMailboxItem: GetMailboxRegister_mailboxesConnection_items
   ): MailboxRegisterListItem {
      return {
         emailAddress: getMailboxItem?.emailAddress ?? null,
         unreadMailboxMessageCount: getMailboxItem?.unreadMailboxMessageCount ?? 0,
         extension: {
            extensionId: getMailboxItem?.extension?.extensionId ?? 0,
            name: getMailboxItem?.extension?.name ?? null,
            number: getMailboxItem?.extension?.number,
            type: getMailboxItem?.extension?.extensionType?.name ?? '',
         },
         mailboxId: getMailboxItem?.mailboxId ?? null,
         mailboxMessage: {
            totalCount: getMailboxItem.unreadMailboxMessageCount,
         },
      };
   }
}
